import { useEffect, useState } from "react";
import ApplyCTA from "@/components/shared/ApplyCTA";
import AccessCard from "@/components/landing/AccessCard";
import ProofStrip from "@/components/landing/ProofStrip";
import { ENV } from "@/lib/env";

export default function Hero() {
  const [phase, setPhase] = useState(0);
  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) { setPhase(6); return; }
    const t = [100, 250, 400, 550, 700].map((d, i) => setTimeout(() => setPhase(i + 1), d));
    return () => t.forEach(clearTimeout);
  }, []);

  return (
    <section className="relative pt-36 md:pt-44 pb-24 md:pb-32 overflow-hidden">
      {/* Atmosphere */}
      <div className="ambient-gold absolute inset-0 pointer-events-none -z-10" />
      <div
        aria-hidden
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[600px] rounded-full pointer-events-none -z-10"
        style={{ background: "rgba(201,169,110,0.05)", filter: "blur(120px)" }}
      />

      <div className="access-container relative">
        {/* Top: headline + access card */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* Content — 7 cols */}
          <div className="lg:col-span-7 space-y-9 md:space-y-10">
            <div className={`flex items-center gap-3 transition-all duration-700 ${phase >= 1 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"}`}>
              <span
                className="w-1.5 h-1.5 rounded-full"
                style={{
                  background: "var(--gold)",
                  boxShadow: "0 0 8px var(--gold)",
                  animation: "status-pulse 2s ease-in-out infinite",
                }}
              />
              <span
                style={{ fontFamily: "'JetBrains Mono', monospace" }}
                className="text-[10px] tracking-[0.3em] uppercase font-medium"
              >
                <span style={{ color: "var(--gold-strong)" }}>Private Sales Program</span>
                <span style={{ color: "var(--gold)" }} className="mx-2">·</span>
                <span style={{ color: "var(--gold-strong)" }}>By Application</span>
              </span>
            </div>

            <h1
              className={`italic text-balance transition-all duration-1000 delay-100 ${
                phase >= 2 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"
              }`}
              style={{
                fontFamily: "'Fraunces', serif",
                fontVariationSettings: "'opsz' 144, 'SOFT' 40",
                fontWeight: 400,
                fontSize: "clamp(3rem, 7vw, 6rem)",
                lineHeight: 0.95,
                letterSpacing: "-0.025em",
                color: "var(--bone)",
              }}
            >
              Become the closer that{" "}
              <span className="inline-block relative" style={{ color: "var(--gold-strong)" }}>
                real offers
                <svg
                  aria-hidden
                  className="absolute -bottom-2 left-0 w-full"
                  viewBox="0 0 300 12"
                  fill="none"
                  preserveAspectRatio="none"
                  style={{ height: "0.18em" }}
                >
                  <path
                    d="M2 10C50 3 150 3 298 10"
                    stroke="var(--gold)"
                    strokeWidth="2"
                    strokeLinecap="round"
                    opacity="0.45"
                  />
                </svg>
              </span>{" "}
              actually want.
            </h1>

            <p
              className={`max-w-xl text-[1.0625rem] md:text-[1.1875rem] leading-[1.65] font-light transition-opacity duration-700 delay-200 ${
                phase >= 3 ? "opacity-100" : "opacity-0"
              }`}
              style={{ color: "var(--text-secondary)" }}
            >
              Four one-to-one trainings. The full framework library. An AI roleplay trainer. A private room of operators. And a direct line to real paid offers when you're ready for them.
            </p>

            <div
              className={`flex flex-col sm:flex-row items-start sm:items-center gap-6 sm:gap-8 pt-2 transition-all duration-700 delay-400 ${
                phase >= 4 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"
              }`}
            >
              <ApplyCTA />
              <div className="flex flex-col gap-0.5">
                <span
                  style={{ fontFamily: "'JetBrains Mono', monospace", color: "var(--text-muted)" }}
                  className="text-[10px] tracking-[0.22em] uppercase"
                >
                  Availability
                </span>
                <span
                  style={{ fontFamily: "'JetBrains Mono', monospace", color: "var(--bone)" }}
                  className="text-[11px] tracking-[0.22em] uppercase"
                >
                  12 Spots · This Month
                </span>
              </div>
            </div>
          </div>

          {/* Access card — 5 cols */}
          <div
            className={`lg:col-span-5 relative flex justify-center lg:justify-end transition-all duration-1000 delay-300 ${
              phase >= 5 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
            }`}
          >
            <div className="relative w-full max-w-[480px]">
              {/* Stage halo */}
              <div
                aria-hidden
                className="absolute -inset-8 rounded-[32px] pointer-events-none"
                style={{
                  background: "rgba(201,169,110,0.10)",
                  filter: "blur(60px)",
                }}
              />
              <div className="relative">
                <AccessCard />
              </div>

              {/* Card caption */}
              <div className="mt-8 flex flex-col items-center gap-3 opacity-60">
                <div
                  aria-hidden
                  className="w-px h-8"
                  style={{ background: "linear-gradient(to top, var(--gold), transparent)" }}
                />
                <span
                  style={{ fontFamily: "'JetBrains Mono', monospace", color: "var(--bone)" }}
                  className="text-[9px] tracking-[0.32em] uppercase text-center"
                >
                  Physical access key issued upon acceptance
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* VSL — large, below */}
        <div
          id="vsl"
          className={`mt-24 md:mt-32 max-w-6xl mx-auto transition-all duration-1000 delay-500 ${
            phase >= 5 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          <div className="vsl-frame relative surface-elev" style={{ aspectRatio: "16 / 9" }}>
            <span className="vsl-corner-bl" />
            <span className="vsl-corner-br" />
            {ENV.VSL_URL ? (
              <iframe
                src={ENV.VSL_URL}
                title="Closer Academy Briefing"
                className="absolute inset-0 w-full h-full"
                frameBorder={0}
                allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
                allowFullScreen
              />
            ) : (
              <div
                className="absolute inset-0 flex flex-col items-center justify-center gap-6"
                style={{ background: "linear-gradient(180deg, rgba(201,168,107,0.04), transparent 60%)" }}
              >
                <button
                  type="button"
                  aria-label="Play briefing"
                  className="relative group w-[88px] h-[88px] md:w-[104px] md:h-[104px] rounded-full flex items-center justify-center"
                  style={{ background: "rgba(201,168,107,0.08)", border: "1px solid var(--gold)" }}
                >
                  <span
                    className="absolute inset-0 rounded-full"
                    style={{
                      border: "1px solid rgba(201,168,107,0.4)",
                      animation: "ping 2.5s ease-in-out infinite",
                    }}
                  />
                  <span
                    aria-hidden
                    className="block w-0 h-0 ml-[4px] border-y-[13px] border-y-transparent border-l-[20px]"
                    style={{ borderLeftColor: "var(--gold)" }}
                  />
                </button>
                <span className="mono-label mono-label-gold">Play briefing · 7 min</span>
              </div>
            )}
            <div className="absolute left-4 bottom-4 flex items-center gap-2 z-[3]">
              <span
                className="w-1.5 h-1.5 rounded-full"
                style={{
                  background: "var(--success)",
                  boxShadow: "0 0 6px var(--success)",
                  animation: "status-pulse 2s ease-in-out infinite",
                }}
              />
              <span
                className="font-mono text-[10px] tracking-[0.22em] uppercase"
                style={{ color: "var(--text-muted)" }}
              >
                Live
              </span>
            </div>
            <span
              className="absolute right-4 bottom-4 font-mono text-[10px] tracking-[0.22em] uppercase z-[3]"
              style={{ color: "var(--text-muted)" }}
            >
              CA · 001
            </span>
          </div>

          {/* Scroll affordance pointing to VSL */}
          <div className="mt-10 flex flex-col items-center gap-3 opacity-70">
            <span
              style={{ fontFamily: "'JetBrains Mono', monospace", color: "var(--text-muted)" }}
              className="text-[9px] tracking-[0.4em] uppercase"
            >
              Watch the 7-min breakdown
            </span>
            <div
              className="w-5 h-8 rounded-full flex justify-center p-1"
              style={{ border: "1px solid rgba(245,241,232,0.18)" }}
            >
              <span
                className="w-0.5 h-1.5 rounded-full"
                style={{ background: "var(--gold)", animation: "scroll-dot 1.8s ease-in-out infinite" }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Count-up proof strip */}
      <ProofStrip />
    </section>
  );
}
